import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, Renderer2, ViewChild } from "@angular/core";
import { TableHeightService } from "../../services/table-height.service";
import { Subscription, debounceTime } from "rxjs";
import { FilterModelDto } from "../../models/filter.model";
import { SupportDataService } from "./services/support-data.service";
import { SupportDetailDto, SupportItemDto } from "./models/support.models";
import { FormSelectModelDto } from "../payment-link-list/models/paylinks.model";
import { SupportDetailUpdateService } from "./services/support-detail-update.service";
import { NotificationService } from "../../services/notification.service";
import { ModalService } from "../../services/modal.service";
import { FilterSettingService } from "src/app/services/filter-setting.service";
import { PermissionService } from "src/app/services/permission.service";

@Component({
	selector: "app-support",
	templateUrl: "./support.component.html",
	styleUrls: ["./support.component.scss"],
})
export class SupportComponent implements OnInit, OnDestroy, AfterViewInit {
	@ViewChild("supportBlock") supportBlock: ElementRef | undefined;
	@ViewChild("tableBody") tableBody: ElementRef | undefined;
	@ViewChild("contentBlock") contentBlock: ElementRef | undefined;

	isOpenFilter = false;

	subs: Subscription[] = [];
	delta = 150;

	currentPage = 0;
	lastPage!: boolean;
	supportList: SupportItemDto[] = [];
	selectedSupportItem: SupportDetailDto | undefined;

	filterAdditional: FilterModelDto[] | undefined;
	filterStatuses: FilterModelDto[] | undefined;
	filterSupportTypes: FilterModelDto[] | undefined;

	supportTypeList: FormSelectModelDto[] = [];
	supportTypeSelected: FormSelectModelDto | undefined;
	supportStatusSelected: FormSelectModelDto | undefined;
	supportStatusList: FormSelectModelDto[] = [];

	isOpenAddContactingDialog = false;
	isOpenDetailContactingDialog = false;

	filterData: any;
	searchWord: string | undefined = "";

	constructor(
		private tableHeightService: TableHeightService,
		private renderer: Renderer2,
		private supportDataService: SupportDataService,
		private supportDetailUpdateService: SupportDetailUpdateService,
		private notificationService: NotificationService,
		private modalService: ModalService,
		private filterSettingService: FilterSettingService,
		public permissionService: PermissionService
	) {}

	ngOnInit() {
		this.initFilter();
		this.getSupportList();
		this.initModal();
	}

	ngOnDestroy() {
		this.subs.forEach((item) => item.unsubscribe());
	}

	ngAfterViewInit() {
		this.subs.push(
			this.tableHeightService.supportHeight$.subscribe({
				next: (res) => {
					this.renderer.setStyle(this.supportBlock?.nativeElement, "height", res);
				},
			})
		);
	}

	onScroll(event: any): void {
		const tableBodyHeight = this.supportBlock?.nativeElement.clientHeight;
		const tableBlockHeight = this.tableBody?.nativeElement.clientHeight;
		const scrollTable = event.target.scrollTop;
		if (tableBlockHeight - tableBodyHeight - scrollTable < this.delta && !this.lastPage) {
			this.getSupportList();
		}
	}

	@HostListener("document:scroll", ["$event"])
	onWindowScroll(event: any): void {
		if (window.innerWidth <= 768) {
			if (this.contentBlock?.nativeElement.clientHeight - window.scrollY - window.innerHeight + 120 < this.delta && !this.lastPage) {
				this.getSupportList();
			}
		}
	}

	initModal() {
		this.subs.push(
			this.modalService.isModalOpen$.subscribe({
				next: (res) => {
					if (!res) {
						this.isOpenAddContactingDialog = false;
						this.isOpenDetailContactingDialog = false;
					}
				},
			})
		);
	}

	getSupportList() {
		this.lastPage = true;
		this.supportDataService.loadSupportList(this.currentPage, this.searchWord, this.filterData).subscribe({
			next: (res) => {
				this.supportList.push(...res.content);
				this.currentPage++;
				this.lastPage = res.last;
			},
		});
	}

	initFilter() {
		this.subs.push(
			this.supportDataService.loadSupportFilter().subscribe({
				next: (res) => {
					this.filterStatuses = res["statuses"];
					this.filterAdditional = res["additional_params"];
					this.filterSupportTypes = res["support_types"];
					this.supportTypeList = res["support_types"];
					this.supportStatusList = res["statuses"];
				},
			})
		);

		this.subs.push(
			this.filterSettingService.filterSetData$.subscribe({
				next: (res) => {
					this.filterData = res;
					this.currentPage = 0;

					this.selectedSupportItem = undefined;
					this.supportList = [];
					this.getSupportList();
				},
			})
		);

		this.subs.push(
			this.filterSettingService.inputSearch$.pipe(debounceTime(300)).subscribe({
				next: (res) => {
					this.supportDataService.loadSearchList(res).subscribe({
						next: (res) => {
							this.filterSettingService.responseSearchData$.next(res.content);
						},
					});
				},
			})
		);

		this.subs.push(
			this.filterSettingService.filterSearchWord$.subscribe({
				next: (res) => {
					this.searchWord = res;
				},
			})
		);

		this.subs.push(
			this.filterSettingService.filterAllReset$.subscribe({
				next: () => {
					this.searchWord = "";
					this.filterData = undefined;
					this.currentPage = 0;

					this.supportList = [];
					this.getSupportList();
				},
			})
		);
	}

	openModalAdd() {
		this.isOpenAddContactingDialog = true;
	}

	toggleFilter(filterState: boolean) {
		this.isOpenFilter = filterState;
	}

	openDetailSupport(id: string) {
		if (this.permissionService.hasPermission(["SUPPORT_VIEW"])) {
			if (this.selectedSupportItem?.support_id === id) {
				this.selectedSupportItem = undefined;
			} else {
				this.selectedSupportItem = this.supportList.find((el) => el.support_id === id);
				this.supportDataService.loadDetailSupport(id).subscribe({
					next: (res) => {
						this.selectedSupportItem = res;
						this.supportStatusSelected = this.supportStatusList.find((item) => item.value === res.status);
						this.supportTypeSelected = this.supportTypeList.find((item) => item.value === res.support_type);

						this.supportDetailUpdateService.selectedSupportItem$.next(this.selectedSupportItem);
						this.supportDetailUpdateService.supportStatusSelected$.next(this.supportStatusSelected);
						this.supportDetailUpdateService.supportTypeSelected$.next(this.supportTypeSelected);
					},
				});
			}

			if (window.innerWidth < 1400) {
				this.isOpenDetailContactingDialog = true;
			}
		} else {
			this.notificationService.setConfiguration(
				"icon-6",
				"error",
				"ROLE_PAGE.NOTIFICATION.PERMISSION_ERROR",
				"ROLE_PAGE.NOTIFICATION.PERMISSION_DESCRIPTION_1"
			);
			this.notificationService.showNotification();
		}
	}

	openCreatedNotification() {
		this.notificationService.setConfiguration(
			"icon-4",
			"success",
			"SUPPORT_PAGE.NOTIFICATION.CREATED.TITLE",
			"SUPPORT_PAGE.NOTIFICATION.CREATED.DESCRIPTION"
		);
		this.notificationService.showNotification();

		this.supportList = [];
		this.currentPage = 0;
		this.getSupportList();
	}

	openEditedNotification() {
		this.notificationService.setConfiguration(
			"icon-1",
			"warning",
			"SUPPORT_PAGE.NOTIFICATION.EDITED.TITLE",
			"SUPPORT_PAGE.NOTIFICATION.EDITED.DESCRIPTION"
		);

		this.notificationService.showNotification();

		this.supportList = [];
		this.currentPage = 0;
		this.getSupportList();

		this.supportDataService.loadDetailSupport(this.selectedSupportItem!.support_id).subscribe({
			next: (res) => {
				this.selectedSupportItem = res;
				this.supportStatusSelected = this.supportStatusList.find((item) => item.value === res.status);
				this.supportTypeSelected = this.supportTypeList.find((item) => item.value === res.support_type);

				this.supportDetailUpdateService.selectedSupportItem$.next(this.selectedSupportItem);
				this.supportDetailUpdateService.supportStatusSelected$.next(this.supportStatusSelected);
				this.supportDetailUpdateService.supportTypeSelected$.next(this.supportTypeSelected);
			},
		});
	}

	openNotDownloadNotification() {
		this.notificationService.setConfiguration("icon-6", "error", "SUPPORT_PAGE.NOTIFICATION.EDITED.TITLE", [
			"SUPPORT_PAGE.NOTIFICATION.NOT_DOWNLOAD.DESCRIPTION",
			"SUPPORT_PAGE.NOTIFICATION.NOT_DOWNLOAD.DESCRIPTION_1",
			"SUPPORT_PAGE.NOTIFICATION.NOT_DOWNLOAD.DESCRIPTION_2",
			"SUPPORT_PAGE.NOTIFICATION.NOT_DOWNLOAD.DESCRIPTION_3",
		]);

		this.notificationService.showNotification();
	}
}
